import FormHelperText from '@mui/material/FormHelperText';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import * as React from 'react';
import { FieldDataType } from '../../../../../sic-lib/enums/FieldDataType';
import { ConversationGridFiltersActions } from '../../../../../sic-lib/reducers/conversationGridFiltersReducer';
import { GenericReducer, genericReducer } from '../../../../../sic-lib/reducers/genericReducer';

type Labels = Record<FieldNames, FieldTypeLabels>;
type FieldNames = 'value' | 'value2';
type FieldTypeLabels = Record<'text' | 'number' | 'date', string>;

const labels: Labels = {
    value: {
        date: 'Start Date',
        number: 'Minimum',
        text: 'Search',
    },
    value2: {
        date: 'End Date',
        number: 'Maximum',
        text: 'Search',
    },
};

interface ComponentState {
    value: string | number;
    label: string;
    type: 'text' | 'number' | 'date';
}

type Props = Pick<TextFieldProps, 'error' | 'helperText'> & {
    /* TIP: fieldName property will be used as the object key in the onChangeHandler reducer */
    fieldName: FieldNames;
    fieldId: number;
    fieldType: FieldDataType;
    fieldValue: string | number;
    operand: string;
    formDispatch: React.Dispatch<ConversationGridFiltersActions>;
};

export default function FilterFastField({
    fieldId,
    fieldType,
    fieldName,
    fieldValue,
    operand,
    formDispatch,
    ...props
}: Props) {
    const [compState, compDispatch] = React.useReducer<GenericReducer<ComponentState>>(
        genericReducer,
        {
            value: fieldValue,
            label: 'Search',
            type: 'text',
        },
    );

    React.useEffect(() => {
        let inputType: 'text' | 'number' | 'date' = 'text';

        switch (fieldType) {
            case FieldDataType.float:
            case FieldDataType.integer:
                inputType = 'number';
                break;
            case FieldDataType.date:
                inputType = 'date';
                break;
            default:
                inputType = 'text';
        }

        compDispatch({
            type: inputType,
            label: operand === 'between' ? labels[fieldName][inputType] : 'Search',
        });
    }, [fieldType, operand, fieldName]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        compDispatch({ value: event.target.value });
    };

    /* TIP: update form object on blur to prevent slow text response on large forms */
    const handleBlur = () => {
        formDispatch({
            type: 'MODIFY_FORM_FILTER',
            fieldId: fieldId,
            condition: {
                [fieldName]: compState.value,
            },
        });
    };

    return (
        <React.Fragment>
            <TextField
                {...(props as TextFieldProps)}
                id={`fast_field_${fieldId}`}
                type={compState.type}
                name={fieldName}
                InputLabelProps={{
                    shrink: compState.type === 'date' || compState.value.toString().length > 0,
                }}
                label={compState.label}
                value={compState.value}
                disabled={operand === 'is not empty'}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                size="small"
                required
            />
            {compState.type === 'text' && operand === 'in' && (
                <FormHelperText>Separate individual searches with a comma</FormHelperText>
            )}
        </React.Fragment>
    );
}
